import { NavigationProp, RouteProp, useRoute } from "@react-navigation/native";
import {
  DEFAULT_PAGE_WIDTH,
  Divider,
  FlatListSearchable,
  Page,
  useThemeProvider,
  View,
  VStack,
} from "@towersystems/roam-common-native";
import {
  LayoutNodeFilter,
  Product,
} from "@towersystems/roam-common/lib/generated-types";
import { Q } from "@nozbe/watermelondb";
import { useCallback, useEffect } from "react";
import { productsQuery } from "./constants";
import { RegisterRootParamList } from "../../../../types";
import { makeEventNotifier } from "../../../../utilities/events";
import { useFilterMetadata } from "../../../../utilities/filter-metadata";
import { filterTypeToFilterValueMetaType } from "../../../../utilities/layout";
import {
  useLoadMoreList,
  useOfflineLoadMoreList,
} from "../../../../utilities/load-more-list";
import {
  useMakeBaseProductQuery,
  filterTypeToProductFilterMap,
  graphqlVariableFormatter,
} from "../../../../utilities/product";
import { useScheduler } from "../../../../utilities/scheduler";
import { useSearch } from "../../../../utilities/search";
import { ProductListItem } from "../../components/ProductListItem";
import { FilterChipsList } from "./components";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";

export type ProductFilteredSearchScreenConfig = {
  filter: LayoutNodeFilter;
  productId?: string;
};

const notifier = makeEventNotifier<{
  productId: string;
}>("OnProductFilteredChange");

export function showProductFilteredSearch(
  navigation: NavigationProp<any, any>,
  config: {
    filter: LayoutNodeFilter;
    productId?: string;
  }
) {
  navigation.navigate("ProductFilteredListScreen", config);
}

export function useProductFilteredSearchListener(
  listener: typeof notifier.notify,
  deps: ReadonlyArray<any>
) {
  notifier.useEventListener(listener, deps);
}

export function ProductFilteredListScreen({ navigation }: any) {
  const {
    params: { filter },
  } = useRoute<RouteProp<RegisterRootParamList, "ProductFilteredListScreen">>();

  function onSelect(product: Product) {
    notifier.notify({
      productId: product.id,
    });
    navigation.goBack();
  }

  return (
    <ProductFilteredListScreenInner
      filter={filter}
      onSelect={onSelect}
      initialLoad={true}
    />
  );
}

export type ProductListScreenProps = {
  filter?: LayoutNodeFilter;
  onSelect: (product: Product) => void;
  initialLoad?: boolean;
};

function ProductFilteredListScreenInner({
  filter,
  onSelect,
  initialLoad,
}: ProductListScreenProps) {
  const filterMetadataContext = useFilterMetadata();
  const { colors, spacings } = useThemeProvider();
  const search = useSearch();
  const { schedule } = useScheduler(500);

  const { makeBaseProductQuery } = useMakeBaseProductQuery();

  const online = useOnline();

  const offlineBaseQuery = makeBaseProductQuery(
    search.query,
    filter
      ? {
          filters: {
            [filterTypeToProductFilterMap[filter.type]]: filter.values,
          },
        }
      : undefined
  );

  const loadMoreOnlineList = useLoadMoreList<Product>({
    query: productsQuery,
    resultKey: "products",
    sort: { updatedAt: "DESC" },
    initialLoad,
    variableFormatter: (v) => {
      let f = {
        ...graphqlVariableFormatter(v, filter),
      };
      f.options.filter.active = { eq: true };
      f.options.filter.internal = { eq: false };
      return f;
    },
  });

  const loadMoreOfflineList = useOfflineLoadMoreList({
    query: offlineBaseQuery,
    sortOptions: {
      updatedAt: Q.desc,
    },
    initialLoad,
  });

  const handleLoadMoreOnline = useCallback(
    (reset?: boolean) => {
      const extraFilters = filter
        ? {
            [filterTypeToProductFilterMap[filter.type]]: {
              in: filter.values,
            },
          }
        : {};

      loadMoreOnlineList.loadMore(
        {
          filter: {
            name: { contains: search.query },
            ...extraFilters,
          },
        },
        reset
      );
    },
    [filter?.values, search.query, loadMoreOnlineList.loadMore]
  );

  const handleLoadMoreOffline = useCallback(
    (reset?: boolean) => {
      loadMoreOfflineList.loadMore(offlineBaseQuery, reset);
    },
    [filter?.values, search.query, loadMoreOfflineList.loadMore]
  );

  const handleLoadMore = useCallback(
    (reset?: boolean) => {
      if (online) {
        handleLoadMoreOnline(reset);
      } else {
        handleLoadMoreOffline(reset);
      }
    },
    [
      online,
      filter?.values,
      search.query,
      loadMoreOnlineList.loadMore,
      loadMoreOfflineList.loadMore,
    ]
  );

  useEffect(() => {
    if (search.touched) {
      schedule(() => {
        handleLoadMore(true);
      });
    }
  }, [filter?.values, search.query, search.touched, online]);

  const loadMoreList = online ? loadMoreOnlineList : loadMoreOfflineList;

  const items = loadMoreList.items;

  const renderItem = useCallback(({ item }: { item: Product }) => {
    return (
      <ProductListItem
        product={item as any}
        action={() => handleProductPress(item)}
      />
    );
  }, []);

  function handleProductPress(product: Product) {
    onSelect(product);
  }

  const emptyComponent = undefined;

  useEffect(() => {
    if (filter) {
      filterMetadataContext.queryMetadata({
        type: filterTypeToFilterValueMetaType(filter.type),
        value: filter.values,
      });
    }
  }, [filter?.values]);

  const extraFilterProps = filter
    ? {
        renderSearchBarHeader: (searchMark: React.ReactNode) => {
          return (
            <View
              style={{
                paddingBottom: spacings.s4,
                marginBottom: spacings.s4,
                borderBottomWidth: 2,
                borderBottomColor: colors.borderColor,
              }}
            >
              <VStack spacing={spacings.s4}>
                {searchMark}
                <FilterChipsList {...{ filter }} />
              </VStack>
            </View>
          );
        },
      }
    : {};

  return (
    <Page
      titleContainerStyle={{ alignItems: "center" }}
      padding={0}
      fullWidth
      scrollable={false}
    >
      <FlatListSearchable
        style={{
          marginBottom: spacings.s5,
        }}
        contentContainerStyle={{
          maxWidth: DEFAULT_PAGE_WIDTH,
          width: "100%",
          alignSelf: "center",
          paddingHorizontal: spacings.s5,
          paddingTop: spacings.s5,
        }}
        placeholder="Search..."
        onChange={search.setQuery}
        value={search.query}
        ItemSeparatorComponent={Divider}
        renderItem={renderItem}
        //refreshing={loading}
        data={items as any}
        scrollEnabled={true}
        ListEmptyComponent={emptyComponent}
        onEndReached={() => {
          return items.length >= 1 && handleLoadMore(false);
        }}
        onEndReachedThreshold={0.3}
        {...extraFilterProps}
      />
    </Page>
  );
}
